<template>
    <div>
        <div class="step">
            <StepIndicator title="1" :isValid="steps.one.isReady" />
            <div class="step__container">
                <div :class="`step__form ${stepsValidationClasses.one}`">
                    <div class="form__header">
                        {{ $t('nBox.form.stepOne') }}
                    </div>
                    <div class="form__body">
                        <b-row>
                            <b-col cols="12" md="11">
                                <b-row>
                                    <b-col sm="12" lg="4" class="mb-0 mb-sm-2">
                                        <Title
                                            :value.sync="$v.form.title.$model"
                                            :state="
                                                checkFieldState($v.form.title)
                                            "
                                        />
                                    </b-col>
                                    <b-col
                                        sm="12"
                                        md="6"
                                        lg="4"
                                        class="mb-0 mb-sm-2"
                                    >
                                        <RowsAndColumns
                                            :rowsState="rowsState"
                                            :columnsState="columnsState"
                                            :rows.sync="$v.form.rows.$model"
                                            :columns.sync="
                                                $v.form.columns.$model
                                            "
                                        />
                                    </b-col>
                                    <b-col
                                        sm="12"
                                        md="6"
                                        lg="4"
                                        class="mb-0 mb-sm-2"
                                    >
                                        <CalibrationType
                                            :value.sync="
                                                $v.form.calibration_type.$model
                                            "
                                            :state="
                                                this.checkFieldState(
                                                    $v.form.calibration_type
                                                )
                                            "
                                        />
                                    </b-col>
                                    <b-col
                                        cols="12"
                                        class="d-flex align-items-end"
                                    >
                                        <Periods
                                            lazyLoading
                                            notGetActive
                                            class="w-100"
                                            style="max-width: 600px"
                                            applyScopeNotArchived
                                            :applyDebounce="false"
                                            :closeOnSelect="false"
                                            :hideSelectedOptions="false"
                                            :label="$t('form.label.periods')"
                                            :value.sync="$v.form.periods.$model"
                                            :numberOfSelectedOptionsToDisplay="
                                                3
                                            "
                                            :invalidFeedback="
                                                $t('form.validations.required')
                                            "
                                            :state="
                                                this.checkFieldState(
                                                    $v.form.periods
                                                )
                                            "
                                            :placeholder="
                                                $t(
                                                    'form.placeholder.selectOneOrMore'
                                                )
                                            "
                                        />
                                    </b-col>
                                </b-row>
                            </b-col>
                            <b-col
                                v-if="!steps.one.isReady"
                                class="d-flex justify-content-end align-items-center"
                            >
                                <b-button
                                    size="sm"
                                    variant="primary"
                                    style="max-height: 28px"
                                    @click="onClickAdvanceStep('one')"
                                >
                                    {{ $t('form.button.advance') }}
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                </div>
            </div>
        </div>
        <div class="step">
            <StepIndicator title="2" :isValid="steps.two.isReady" />
            <div v-if="steps.one.isReady" class="step__container">
                <div :class="`step__form ${stepsValidationClasses.two}`">
                    <div class="form__header">
                        {{ $t('nBox.form.stepTwo') }}
                    </div>
                    <div class="form__body">
                        <b-row>
                            <b-col
                                :key="key"
                                lg="6"
                                v-for="(key, axis) in {
                                    x: 'axis_x',
                                    y: 'axis_y',
                                }"
                            >
                                <AxisSetup
                                    :axis="axis"
                                    :validations="axisValidations[key]"
                                    :variables.sync="form[key].variables"
                                    :axisTitle.sync="$v.form[key].title.$model"
                                    @update-variable="
                                        (index, prop, value) =>
                                            onUpdateVariable(
                                                key,
                                                index,
                                                prop,
                                                value
                                            )
                                    "
                                />
                                <hr
                                    v-if="key == 'axis_x'"
                                    class="d-block d-lg-none mb-4 mt-4"
                                />
                            </b-col>
                        </b-row>
                        <div
                            v-if="!steps.two.isReady"
                            class="d-flex justify-content-end"
                        >
                            <b-button
                                size="sm"
                                variant="primary"
                                @click="onClickAdvanceStep('two')"
                            >
                                {{ $t('form.button.advance') }}
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="step">
            <StepIndicator title="3" :isValid="steps.three.isReady" />
            <div v-if="steps.two.isReady" class="step__container">
                <div :class="`step__form ${stepsValidationClasses.three}`">
                    <div class="form__header">
                        {{ $t('nBox.form.stepThree') }}
                    </div>
                    <div class="form__body">
                        <Quadrants
                            :rows="form.rows"
                            :axisXTitle="form.axis_x.title"
                            :axisYTitle="form.axis_y.title"
                            :rowsState="!$v.form.rows.$invalid"
                            :columns="form.columns"
                            :columnsState="!$v.form.columns.$invalid"
                            :quadrants.sync="form.quadrants"
                            :quadrantsValidations="quadrantsValidations"
                            :limits.sync="form.limits"
                            :limitsValidations="limitsValidations"
                            :targetQuadrantValidation="targetQuadrantValidation"
                            @update-limit-item="onUpdateLimitItem"
                            @update-quadrant="onUpdateQuadrantProperty"
                        />
                        <div class="d-flex justify-content-between">
                            <AxisLimits
                                class="mt-3"
                                :quadrants="form.quadrants"
                                :validations="axisLimitsValidations"
                                :xAxis.sync="$v.form.x_target_value.$model"
                                :yAxis.sync="$v.form.y_target_value.$model"
                            />
                            <b-button
                                v-if="!steps.three.isReady"
                                size="sm"
                                variant="primary"
                                class="align-self-center"
                                style="max-height: 28px"
                                @click="onClickAdvanceStep('three')"
                            >
                                {{ $t('form.button.advance') }}
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="step">
            <StepIndicator
                title="4"
                :isValid="stepsValidationClasses.four == 'valid'"
            />
            <div v-if="steps.three.isReady" class="step__container">
                <div :class="`step__form ${stepsValidationClasses.four}`">
                    <div class="form__header">
                        <span class="mr-2">{{ $t('nBox.form.stepFour') }}</span>
                        <i
                            v-b-tooltip.hover
                            class="far fa-question-circle text-muted"
                            :title="
                                $t('nBox.form.definitionOfTheTargetAudience')
                            "
                        />
                    </div>
                    <div class="form__body">
                        <b-row>
                            <b-col cols="12" lg="8">
                                <Teams
                                    multiple
                                    lazyLoading
                                    enableGroupSelect
                                    labelClass="label"
                                    :sectionsType="['department']"
                                    :value.sync="$v.form.teams.$model"
                                    :label="$t('form.label.departments')"
                                    :placeholder="
                                        $t('form.placeholder.selectOneOrMore')
                                    "
                                    :state="checkFieldState($v.form.teams)"
                                />
                            </b-col>
                            <b-col>
                                <Status
                                    :lazyLoading="true"
                                    :closeOnSelect="false"
                                    labelClass="label"
                                    :label="$t('form.label.userStatus')"
                                    :placeholder="
                                        $t('form.placeholder.selectOneOrMore')
                                    "
                                    :selected.sync="$v.form.status.$model"
                                    :state="checkFieldState($v.form.status)"
                                >
                                    <template slot="invalid-feedback">
                                        <div>
                                            {{
                                                $t('form.validations.required')
                                            }}
                                        </div>
                                    </template>
                                </Status>
                            </b-col>
                            <b-col cols="12" lg="8">
                                <OccupationGroups
                                    showLabel
                                    enableSelectAll
                                    :closeOnSelect="false"
                                    :placeholder="
                                        $t('form.placeholder.selectOneOrMore')
                                    "
                                    :state="
                                        checkFieldState(
                                            $v.form.occupation_groups
                                        )
                                    "
                                    :value.sync="
                                        $v.form.occupation_groups.$model
                                    "
                                >
                                    <template slot="label">
                                        <span class="label">
                                            {{
                                                $t(
                                                    'form.label.occupationGroups'
                                                )
                                            }}
                                        </span>
                                    </template>
                                </OccupationGroups>
                                <b-form-invalid-feedback
                                    v-if="
                                        checkFieldDirty(
                                            $v.form.occupation_groups
                                        ) || checkFieldDirty($v.form.teams)
                                    "
                                    :state="
                                        checkFieldState(
                                            $v.form.occupation_groups
                                        ) || checkFieldState($v.form.teams)
                                    "
                                >
                                    {{
                                        $t(
                                            'nBox.form.validations.teamsOrOccupationGroupRequired'
                                        )
                                    }}
                                </b-form-invalid-feedback>
                            </b-col>
                            <b-col>
                                <AdmittedToDate
                                    :state="
                                        checkFieldState($v.form.admission_date)
                                    "
                                    :value.sync="$v.form.admission_date.$model"
                                />
                            </b-col>
                        </b-row>

                        <b-form-invalid-feedback
                            v-if="$v.form.$dirty"
                            class="mb-2"
                            :state="formIsValid"
                            style="text-align: end"
                        >
                            {{ generalValidationText }}
                        </b-form-invalid-feedback>
                        <div class="d-flex justify-content-end">
                            <b-button
                                size="sm"
                                class="mr-2"
                                @click="$emit('cancel')"
                            >
                                {{ $t('form.button.cancel') }}
                            </b-button>
                            <ButtonLoader
                                size="sm"
                                variant="primary"
                                :loader="isLoading"
                                :disabled="isLoading"
                                @click="onClickSubmit"
                                >{{ $t('form.button.save') }}</ButtonLoader
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <StepIndicator withoutLine :isValid="formIsValid">
            <i class="fas fa-check" />
        </StepIndicator>
    </div>
</template>
<style scoped src="./styles.scss" lang="scss"></style>
<script>
import i18n from '@/i18n'
import validations from './validations'
import { validationMixin } from 'vuelidate'
import ButtonLoader from '@/views/components/button-loader'
import {
    Status,
    Periods,
    OccupationGroups,
    Sections as Teams,
} from '@/views/components/fields'
import {
    Title,
    Quadrants,
    AxisSetup,
    AxisLimits,
    AdmittedToDate,
    StepIndicator,
    RowsAndColumns,
    CalibrationType,
} from './components'

export default {
    mixins: [validationMixin],
    components: {
        Teams,
        Title,
        Status,
        Periods,
        AxisSetup,
        Quadrants,
        AxisLimits,
        ButtonLoader,
        StepIndicator,
        RowsAndColumns,
        AdmittedToDate,
        CalibrationType,
        OccupationGroups,
    },
    validations: {
        form: { ...validations },
    },
    props: {
        data: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            steps: {
                one: {
                    isReady: this.data !== null,
                    fields: [
                        'title',
                        'rows',
                        'columns',
                        'calibration_type',
                        'periods',
                    ],
                },
                two: {
                    isReady: this.data !== null,
                    fields: ['axis_x', 'axis_y'],
                },
                three: {
                    isReady: this.data !== null,
                    fields: [
                        'quadrants',
                        'limits',
                        'x_target_value',
                        'y_target_value',
                    ],
                },
                four: {
                    isReady: this.data !== null,
                    fields: [
                        'status',
                        'teams',
                        'occupation_groups',
                        'admission_date',
                    ],
                },
            },
            form: {
                title: null,
                rows: 3,
                columns: 3,
                periods: [],
                x_target_value: 0,
                y_target_value: 0,
                admission_date: null,
                status: [],
                teams: [],
                occupation_groups: [],
                calibration_type: null,
                limits: {
                    x: [],
                    y: [],
                },
                quadrants: [],
                axis_x: {
                    title: null,
                    variables: [],
                },
                axis_y: {
                    title: null,
                    variables: [],
                },
            },
        }
    },
    computed: {
        axisLimitsValidations() {
            return ['x', 'y'].reduce((object, key) => {
                const field = this.$v.form[`${key}_target_value`]
                return {
                    ...object,
                    [key]: {
                        state: this.checkFieldState(field),
                        decimal: this.checkFieldValidation(field, 'decimal'),
                        required: this.checkFieldValidation(field, 'required'),
                        minValue: this.checkFieldValidation(field, 'minValue'),
                        maxValue: this.checkFieldValidation(field, 'maxValue'),
                    },
                }
            }, {})
        },
        axisValidations() {
            return ['axis_x', 'axis_y'].reduce((object, key) => {
                const axis = this.$v.form[key]
                object[key] = {
                    axisTitle: {
                        state: this.checkFieldState(axis.title),
                        required: this.checkFieldValidation(
                            axis.title,
                            'required'
                        ),
                        maxLength: this.checkFieldValidation(
                            axis.title,
                            'maxLength'
                        ),
                    },
                    axisVariables: this.checkFieldValidation(
                        axis.variables,
                        'required'
                    ),
                    variables: this.variablesValidations[key],
                }

                return object
            }, {})
        },
        variablesValidations() {
            return ['axis_x', 'axis_y'].reduce((object, axis) => {
                object[axis] = Object.keys(this.form[axis].variables).map(
                    index => {
                        const variable = this.getVariableByAxisAndIndex(
                            axis,
                            index
                        )
                        return this.getVariableValidations(variable)
                    }
                )
                return object
            }, {})
        },
        targetQuadrantValidation() {
            return this.checkFieldValidation(
                this.$v.form.quadrants,
                'targetQuadrant'
            )
        },
        quadrantsValidations() {
            return this.form.quadrants.map((_, index) => {
                const quadrant = this.getQuadrantByIndex(index)
                return {
                    title: this.checkFieldState(quadrant.title),
                    description: this.checkFieldState(quadrant.description),
                }
            })
        },
        rowsState() {
            const rows = this.$v.form.rows
            return {
                state: this.checkFieldState(rows),
                required: rows.required,
                maxValue: rows.maxValue,
                minValue: rows.minValue,
            }
        },
        columnsState() {
            const columns = this.$v.form.columns
            return {
                state: this.checkFieldState(columns),
                required: columns.required,
                maxValue: columns.maxValue,
                minValue: columns.minValue,
            }
        },
        limitsValidations() {
            return ['x', 'y'].reduce((object, axis) => {
                const validations = []
                const model = this.$v.form.limits[axis]
                const items = model.$each.$iter
                let limitsAreValid = this.checkFieldValidation(
                    model,
                    'quadrantsLimits'
                )
                limitsAreValid = limitsAreValid == null ? true : limitsAreValid

                for (const index in items) {
                    const item = items[index]
                    const isDirty = this.checkFieldDirty(item)
                    validations.push({
                        isValid: isDirty
                            ? this.checkFieldState(item) && limitsAreValid
                            : null,
                        quadrantsLimits: limitsAreValid,
                        decimal: this.checkFieldValidation(item, 'decimal'),
                        minValue: this.checkFieldValidation(item, 'minValue'),
                    })
                }

                return {
                    ...object,
                    [axis]: validations,
                }
            }, {})
        },
        stepsValidations() {
            return Object.keys(this.steps).reduce((object, step) => {
                const form = this.$v.form
                const fields = this.steps[step].fields
                object[step] = fields.every(field =>
                    this.checkFieldState(form[field])
                )
                return object
            }, {})
        },
        generalValidationText() {
            const and = i18n.t('common.and')
            const sections = Object.keys(this.stepsValidations)
                .reduce((array, step, index) => {
                    const isValid = this.stepsValidations[step]
                    if (!isValid) {
                        array.push(index + 1)
                    }
                    return array
                }, [])
                .reduce((string, step, index, steps) => {
                    if (index == 0) {
                        return step
                    }

                    if (index == steps.length - 1) {
                        return `${string} ${and} ${step}`
                    }

                    return `${string}, ${step}`
                }, '')

            return i18n.t('nBox.form.validations.sectionsInvalid', { sections })
        },
        stepsValidationClasses() {
            return Object.keys(this.stepsValidations).reduce((object, step) => {
                const isValid = this.stepsValidations[step]
                object[step] = isValid ? 'valid' : ''
                return object
            }, {})
        },
        formIsValid() {
            return this.checkFieldState(this.$v.form)
        },
    },
    mounted() {
        if (this.data !== null) {
            this.form = this.data
        }
    },
    methods: {
        onClickSubmit() {
            this.$v.$touch()

            if (!this.formIsValid) {
                return
            }

            this.steps.three.isReady = true
            this.$emit('submit', this.form, this.setLoader)
        },
        setLoader(value) {
            this.isLoading = value
        },
        onUpdateVariable(axis, index, property, value) {
            const variable = this.getVariableByAxisAndIndex(axis, index)
            variable[property].$model = value
        },
        onClickAdvanceStep(step) {
            this.touchStepFields(step)
            if (this.stepsValidations[step]) {
                this.steps[step].isReady = true
            }
        },
        touchStepFields(step) {
            const form = this.$v.form
            this.steps[step].fields.forEach(field => form[field].$touch())
        },
        onUpdateLimitItem(index, axis, value) {
            const axisLimits = this.$v.form.limits[axis]
            axisLimits.$each.$iter[index].$model = value
        },
        onUpdateQuadrantProperty(index, property, value) {
            const quadrant = this.getQuadrantByIndex(index)
            quadrant[property].$model = value
        },
        getQuadrantByIndex(index) {
            return this.$v.form.quadrants.$each.$iter[index]
        },
        getVariableValidations(variable) {
            return {
                isValid: this.checkFieldState(variable),
                category_id: this.checkFieldState(variable.category_id),
                result_type: this.checkFieldState(variable.result_type),
                result_weight: this.getVariableWeightValidation(variable),
                personal_weight: this.getVariableLevelWeightValidation(
                    variable,
                    'personal_weight'
                ),
                team_weight: this.getVariableLevelWeightValidation(
                    variable,
                    'team_weight'
                ),
                unit_weight: this.getVariableLevelWeightValidation(
                    variable,
                    'unit_weight'
                ),
            }
        },
        getVariableLevelWeightValidation(variable, level) {
            return {
                isValid: this.checkFieldState(variable[level]),
                minValue: this.checkFieldValidation(
                    variable[level],
                    'minValue'
                ),
                maxValue: this.checkFieldValidation(
                    variable[level],
                    'maxValue'
                ),
                required: this.checkFieldValidation(
                    variable[level],
                    'required'
                ),
                sumOfTargetLevelsWeight: this.checkFieldValidation(
                    variable[level],
                    'sumOfTargetLevelsWeightMustBeEqualOneHundred'
                ),
            }
        },
        getVariableWeightValidation(variable) {
            const sumOfVariablesWeightIsValid = this.$v.form[
                `axis_${variable.$model.axis}`
            ].variables.sumOfVariablesWeightMustBeEqualOneHundred

            const resultsWeightIsValid = this.checkFieldState(
                variable.result_weight
            )
            const dirty = this.checkFieldDirty(variable.result_weight)
            const variableSumOfVariablesWeightIsValid = dirty
                ? sumOfVariablesWeightIsValid
                : null

            return {
                minValue: this.checkFieldValidation(
                    variable.result_weight,
                    'minValue'
                ),
                maxValue: this.checkFieldValidation(
                    variable.result_weight,
                    'maxValue'
                ),
                required: this.checkFieldValidation(
                    variable.result_weight,
                    'required'
                ),
                isValid: resultsWeightIsValid && sumOfVariablesWeightIsValid,
                sumOfVariablesWeight: variableSumOfVariablesWeightIsValid,
            }
        },
        checkFieldState(field) {
            return this.checkFieldDirty(field) ? !field.$invalid : null
        },
        checkFieldValidation(field, validation) {
            return this.checkFieldDirty(field) ? field[validation] : null
        },
        checkFieldDirty(field) {
            return field.$dirty
        },
        onUpdateVariableWeight(axis, index, weight) {
            const variable = this.getVariableByAxisAndIndex(axis, index)
            variable.result_weight.$model = weight
        },
        getVariableByAxisAndIndex(axis, index) {
            return this.$v.form[axis].variables.$each.$iter[index]
        },
    },
}
</script>
